/**
 * Tenant theming — curated accent presets applied as CSS custom properties.
 *
 * Only the --theme-accent-* group is themable (see theme.css); neutrals,
 * danger, success, and shape tokens stay fixed so a tenant's choice can never
 * break contrast or layout. Presets are curated (not arbitrary hex) so every
 * derived shade — tint band, active border, ink-on-tint, focus ring — is
 * hand-checked for contrast.
 *
 * The catalog is spaced by HUE so that no two presets read as the same choice
 * (27° 142° 175° 201° 215°(desaturated) 244° 295° 344°). Two pairs used to
 * collide — teal/emerald sat 15° apart and indigo/violet 18°, close enough
 * that neither could be told from the other in a swatch grid. Emerald moved
 * to a true green and violet to magenta, and Ocean was added because a plain
 * blue is the first colour most dealerships reach for and the list had none.
 *
 * Preset KEYS are stored in the database, so they never change; only labels
 * and hexes do. The server keeps a matching allow-list in
 * node/routes/themeSettings.js — add there first, or the save 400s.
 *
 * The dealership's choice is stored in Dealerships.ThemeSettings (JSON,
 * `{ "preset": "teal" }`) and arrives with the session payload; Cars.jsx
 * calls applyTheme() once on login and again when Settings saves a change.
 */

export const DEFAULT_THEME = "indigo";

export const THEME_PRESETS = {
  indigo: {
    label: "Indigo",
    accent: "#4f46e5",
    hover: "#4338ca",
    tint: "#eef0fd",
    border: "#c7cdf5",
    ink: "#3730a3",
    ring: "rgba(79, 70, 229, 0.28)",
  },
  ocean: {
    label: "Ocean",
    accent: "#0369a1",
    hover: "#075985",
    tint: "#e6f1f8",
    border: "#b8d6e9",
    ink: "#075985",
    ring: "rgba(3, 105, 161, 0.28)",
  },
  teal: {
    label: "Teal",
    accent: "#0f766e",
    hover: "#0c5f59",
    tint: "#e7f1ef",
    border: "#c2ddd7",
    ink: "#115e59",
    ring: "rgba(15, 118, 110, 0.28)",
  },
  //Key stays "emerald" (it is in the database); the colour moved off teal's
  //shoulder to a true green.
  emerald: {
    label: "Forest",
    accent: "#15803d",
    hover: "#166534",
    tint: "#e9f5ec",
    border: "#bfe0c9",
    ink: "#14532d",
    ring: "rgba(21, 128, 61, 0.28)",
  },
  crimson: {
    label: "Crimson",
    accent: "#be123c",
    hover: "#9f1239",
    tint: "#fdecf0",
    border: "#f5c2cf",
    ink: "#9f1239",
    ring: "rgba(190, 18, 60, 0.28)",
  },
  amber: {
    label: "Amber",
    accent: "#b45309",
    hover: "#92400e",
    tint: "#fdf3e7",
    border: "#f2d9b5",
    ink: "#92400e",
    ring: "rgba(180, 83, 9, 0.28)",
  },
  //Key stays "violet" (it is in the database); the colour moved off indigo's
  //shoulder into magenta.
  violet: {
    label: "Plum",
    accent: "#a21caf",
    hover: "#86198f",
    tint: "#fbecfd",
    border: "#eec9f2",
    ink: "#86198f",
    ring: "rgba(162, 28, 175, 0.28)",
  },
  slate: {
    label: "Slate",
    accent: "#475569",
    hover: "#334155",
    tint: "#eef2f6",
    border: "#cbd5e1",
    ink: "#334155",
    ring: "rgba(71, 85, 105, 0.28)",
  },
};

/** Apply a preset's accent group to <html>. Unknown names fall back to the default. */
export function applyTheme(presetName) {
  const preset = THEME_PRESETS[presetName] || THEME_PRESETS[DEFAULT_THEME];
  const s = document.documentElement.style;
  s.setProperty("--theme-accent", preset.accent);
  s.setProperty("--theme-accent-hover", preset.hover);
  s.setProperty("--theme-accent-fg", "#ffffff");
  s.setProperty("--theme-accent-tint", preset.tint);
  s.setProperty("--theme-accent-border", preset.border);
  s.setProperty("--theme-accent-ink", preset.ink);
  s.setProperty("--theme-ring", preset.ring);
  //Browser chrome tint (Android address bar, PWA title bar) follows the
  //tenant accent instead of the static index.html value (plan 5.6).
  const themeColorMeta = document.querySelector('meta[name="theme-color"]');
  if (themeColorMeta) themeColorMeta.setAttribute("content", preset.accent);
}

/**
 * Resolved accent hex for JS consumers that can't take a CSS variable
 * (react-joyride's primaryColor, chart libs). Read at render time so it
 * follows the applied tenant theme.
 */
export function themeAccent() {
  return (
    getComputedStyle(document.documentElement).getPropertyValue("--theme-accent").trim() ||
    THEME_PRESETS[DEFAULT_THEME].accent
  );
}

/** Parse Dealerships.ThemeSettings (JSON string or object) to a preset name. */
export function presetFromThemeSettings(themeSettings) {
  if (!themeSettings) return DEFAULT_THEME;
  try {
    const obj = typeof themeSettings === "string" ? JSON.parse(themeSettings) : themeSettings;
    return obj && THEME_PRESETS[obj.preset] ? obj.preset : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}
